import React, { Fragment, useEffect, useState } from "react";
import { Formik, Form, ErrorMessage } from "formik";
import {
    CheckboxInput,
    CheckboxLabel,
    ErrorText,
    Input,
    InputContainer,
    FieldContainer,
    Label,
    OptionsContainer,
    Submit,
} from "../../components/Footer/FormElements";
import { formApiBaseUrl } from "../../util/baseUrls";
import Spinner from "../../components/Footer/Spinner";
import {Field} from "formik"

const TYPE_MAP = {
    single_line_text: "text",
    email: "email",
    multi_line_text: "textarea",
    multiple_checkboxes: "checkbox",
    radio: "radio",
};

const AS_MAP = {
    single_line_text: "input",
    email: "input",
    multi_line_text: "textarea",
    multiple_checkboxes: "input",
    radio: "input",
};

const LEGAL_CONSENT_BASE_NAME = "LEGAL_CONSENT_subscription_type_";
const generateLegalCheckboxName = subscriptionTypeId => {
    return `${LEGAL_CONSENT_BASE_NAME}${subscriptionTypeId}`;
};

/**
 * Returns a Formik element with Form, Rich Text and Fields from the Hubspot API json
 * @property {string} formData JSON response from the hubspot API
 *
 * @return {Formik}
 */
export const HubspotForm = ({ formData, onSubmit }) => {
    const [groups, setGroups] = useState([]);
    const [isSubmitting, setSubmitting] = useState(false);
    const [hasSubmitted, setSubmitted] = useState(false);
    const [genericErrorMessage, setGenericErrorMessage] = useState(null);
    let { fieldGroups, legalConsentOptions } = formData || {};

    // Formik intialValues
    let initialValues = {first_name: ""};
    if (legalConsentOptions?.communicationsCheckboxes?.length > 0) {
        legalConsentOptions.communicationsCheckboxes.forEach(option => {
            initialValues[
                generateLegalCheckboxName(option.subscriptionTypeId)
            ] = "";
        });
    }
    if (fieldGroups?.length > 0) {
        fieldGroups.forEach(fieldGroup => {
            if (fieldGroup.fields) {
                fieldGroup.fields.forEach(field => {
                    if (field.name === "subscription_source") {
                        // special case
                        initialValues[field.name] = "Virtual Economy";
                    } else {
                        initialValues[field.name] = "";
                    }
                });
            }
        });
    }

    // Formik Fields
    useEffect(() => {
        if (!fieldGroups && !legalConsentOptions) return;

        let newGroups = [];

        if (legalConsentOptions?.communicationsCheckboxes?.length > 0) {
            const theGroup = {};
            theGroup.fields = [];

            const optionFields = [];

            legalConsentOptions.communicationsCheckboxes.forEach(
                (option, optionIndex) => {
                    const id = `legal-consent-checkboxes-o${optionIndex}`;
                    const name = generateLegalCheckboxName(
                        option.subscriptionTypeId
                    );

                    optionFields.push(
                        <ErrorMessage key={`${id}-error`} name={name}>
                            {msg => <div className="error-message">{msg}</div>}
                        </ErrorMessage>
                    );

                    optionFields.push(
                        <OptionsContainer key={id}>
                            <CheckboxInput
                                id={id}
                                name={name}
                                required={option.required}
                                type={TYPE_MAP.multiple_checkboxes}
                                as={AS_MAP.multiple_checkboxes}
                                value="yes"
                            />

                            <CheckboxLabel
                                htmlFor={id}
                                dangerouslySetInnerHTML={{
                                    __html: option.label,
                                }}
                            />
                        </OptionsContainer>
                    );
                }
            );

            theGroup.fields.push(
                <FieldContainer key="legal-consent">
                    {optionFields}
                </FieldContainer>
            );

            if (theGroup) newGroups.push(theGroup);
        }

        if (fieldGroups) {
            fieldGroups.forEach((group, groupIndex) => {
                let theGroup = {};

                // Rich text el
                if (group.richText) {
                    theGroup.richText = (
                        <div
                            key={`g${groupIndex}-rT`}
                            dangerouslySetInnerHTML={{ __html: group.richText }}
                        ></div>
                    );
                }

                // Are there fields in the group to build Elements for
                if (group.fields) {
                    theGroup.fields = [];

                    group.fields.forEach((groupField, groupFieldIndex) => {
                        // Checkbox (not dropdowns yet, test groupField.fieldType for that)
                        if (groupField.options) {
                            theGroup.fields.push(
                                <ErrorMessage
                                    key={`g${groupIndex}-i${groupFieldIndex}-error`}
                                    name={groupField.name}
                                >
                                    {msg => <ErrorMessage>{msg}</ErrorMessage>}
                                </ErrorMessage>
                            );
                            const optionFields = [];

                            groupField.options.forEach(
                                (option, optionIndex) => {
                                    const id = `g${groupIndex}-i${groupFieldIndex}-o${optionIndex}`;

                                    optionFields.push(
                                        <OptionsContainer key={id}>
                                            <CheckboxInput
                                                id={id}
                                                name={groupField.name}
                                                hidden={groupField.hidden}
                                                // required={groupField.required}
                                                type={
                                                    TYPE_MAP[
                                                        groupField.fieldType
                                                    ]
                                                }
                                                as={
                                                    AS_MAP[groupField.fieldType]
                                                }
                                                value={option.value}
                                            />

                                            <CheckboxLabel htmlFor={id}>
                                                <span>{option.label}</span>
                                            </CheckboxLabel>
                                        </OptionsContainer>
                                    );
                                }
                            );

                            theGroup.fields.push(
                                <FieldContainer
                                    key={`g${groupIndex}-i${groupFieldIndex}`}
                                    className={
                                        groupField.hidden ? "hidden" : ""
                                    }
                                >
                                    {groupField.label && (
                                        <Label>{groupField.label}</Label>
                                    )}
                                    {optionFields}
                                </FieldContainer>
                            );
                        } else {
                            // Its a text input
                            theGroup.fields.push(
                                <Fragment
                                    key={`g${groupIndex}-i${groupFieldIndex}`}
                                >
                                    <ErrorMessage name={groupField.name}>
                                        {msg => (
                                            <ErrorMessage>{msg}</ErrorMessage>
                                        )}
                                    </ErrorMessage>
                                    <FieldContainer
                                        className={`${
                                            groupField.hidden ? "hidden" : ""
                                        } ${
                                            groupField.name === "email"
                                                ? "field-container--email"
                                                : ""
                                        }`}
                                    >
                                        <Label htmlFor={groupField.name}>
                                            {groupField.label}
                                        </Label>
                                        <Input
                                            id={groupField.name}
                                            name={groupField.name}
                                            hidden={groupField.hidden}
                                            required={groupField.required}
                                            type={
                                                TYPE_MAP[groupField.fieldType]
                                            }
                                            as={AS_MAP[groupField.fieldType]}
                                            placeholder={
                                                groupField.placeholder ||
                                                groupField.label
                                            }
                                        />
                                    </FieldContainer>
                                </Fragment>
                            );
                        }
                    });
                }
                if (theGroup) newGroups.push(theGroup);
            });
        }
        setGroups(newGroups);
    }, [fieldGroups, legalConsentOptions]);

    function validateRequiredFields(values) {
        let errors = {};
        if (
            formData &&
            formData.fieldGroups &&
            formData.fieldGroups.length > 0
        ) {
            formData.fieldGroups.forEach((fieldGroup, index) => {
                if (fieldGroup.fields && fieldGroup.fields.length > 0) {
                    fieldGroup.fields.forEach((field, index) => {
                        const { name: fieldName } = field;
                        if (
                            field.required &&
                            field.fieldType === "multiple_checkboxes"
                        ) {
                            if (values[fieldName].length === 0) {
                                errors[fieldName] =
                                    "Please select an option from the group below";
                            }
                        }
                    });
                }
            });
        }
        return { ...errors };
    }

    useEffect(() => {
        if (hasSubmitted && onSubmit) {
            onSubmit();
        }
    }, [hasSubmitted, onSubmit]);

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            validate={values => {
                const errors = validateRequiredFields(values);
                return { ...errors };
            }}
            onSubmit={(values, actions) => {
                setSubmitting(true);
                setGenericErrorMessage(null);
                setTimeout(async () => {
                    // Map data to contain {objectTypeId, name, value} objects
                    let data = {
                        fields: [],
                        legalConsentOptions: {
                            consent: {
                                consentToProcess: true,
                                text: legalConsentOptions?.consentToProcessText,
                                communications: [],
                            },
                        },
                    };
                    let fieldKeys = Object.keys(values);
                    if (
                        legalConsentOptions?.communicationsCheckboxes?.length >
                        0
                    ) {
                        legalConsentOptions.communicationsCheckboxes.forEach(
                            option => {
                                data.legalConsentOptions.consent.communications.push(
                                    {
                                        value: values[
                                            generateLegalCheckboxName(
                                                option.subscriptionTypeId
                                            )
                                        ].includes("yes"),
                                        subscriptionTypeId:
                                            option.subscriptionTypeId,
                                        text: option.label,
                                    }
                                );
                            }
                        );
                    }
                    fieldKeys.forEach(key => {
                        const isLegalField = key.includes(
                            LEGAL_CONSENT_BASE_NAME
                        );
                        if (isLegalField) return;
                        // Checkboxes value prop needs to be a chained together string of values split by semicolon e.g:
                        // val1;val2;val3
                        // This is undocumented, luckily this person started a list of gotchas:
                        // https://github.com/HubSpot/hubspot-php/wiki/Forms
                        if (Array.isArray(values[key])) {
                            let valueString = "";
                            values[key].forEach(val => {
                                valueString += `;${val}`;
                            });
                            data.fields.push({
                                objectTypeId: "0-1",
                                name: key,
                                value: valueString.substring(1),
                            });
                        } else {
                            // Normal text inputs
                            data.fields.push({
                                objectTypeId: "0-1",
                                name: key,
                                value: values[key],
                            });
                        }
                    });

                    const formId = formData?.id;

                    try {
                        let submitFetchOptions = {
                            method: "POST",
                            body: JSON.stringify(data),
                        };

                        let response = await fetch(
                            `${formApiBaseUrl}/api/hubspot-form/${formId}/submit`,
                            submitFetchOptions
                        );

                        setSubmitting(false);
                        setSubmitted(response.ok);

                        const responseData = await response.json();

                        if (responseData.error) {
                            // Generic error
                            setGenericErrorMessage(
                                "There was an error submitting the form. Please try again later or contact us."
                            );
                        }
                    } catch (error) {
                        console.error("Error submitting form data", error);
                        setSubmitting(false);
                        setGenericErrorMessage(
                            "There was an error submitting the form. Please try again later or contact us."
                        );
                    }
                }, 2000);
            }}
        >
            <>
                <Spinner visible={isSubmitting} />
                {!hasSubmitted && formData && (
                    <Form>
                        <InputContainer faded={isSubmitting}>
                            {genericErrorMessage && (
                                <ErrorText>{genericErrorMessage}</ErrorText>
                            )}
                            {groups.map((group, index) => {
                                return (
                                    <React.Fragment key={`groups-${index}`}>
                                        {group.fields
                                            ? group.fields.map(
                                                  (field, index) => {
                                                      return (
                                                          <React.Fragment
                                                              key={`group-${index}`}
                                                          >
                                                              {field}
                                                          </React.Fragment>
                                                      );
                                                  }
                                              )
                                            : null}
                                    </React.Fragment>
                                );
                            })}
                            <Field type="text" name="first_name" className="input-helper" />
                            <Submit type="submit" disabled={isSubmitting}>
                                <span>SUBMIT</span>
                            </Submit>
                        </InputContainer>
                    </Form>
                )}
            </>
        </Formik>
    );
};
